import React from 'react';
import { Sprite } from '@pixi/react';
import { useEffect, useRef, useState } from "react";
import * as PIXI from 'pixi.js';
import { boardTileSize } from '../../env';
import spriteAlphaDataCache from './SpriteAlphaDataCache';

const ANIMATION_DURATION = 1000; // milliseconds

const easeOutCubic = ( t ) => ( --t ) * t * t + 1;

const lerp = ( from, to, t ) => from + ( to - from ) * t;

const BoardSprite = (
  {
    x,
    y,
    imageSrc,
    renderScale,
    offsetX = 0,
    offsetY = 0,
    delay = 0,
    textureCache = null,
    animSteps = null,
    onAnimFinish = null,
    ...props
  } ) =>
{
  const [ texture, setTexture ] = useState( textureCache?.[ imageSrc ] || null );
  const [ hasAnimated, setHasAnimated ] = useState( false );
  const [ alphaData, setAlphaData ] = useState( null ); // Cache alpha data for efficient hit detection
  const spriteRef = useRef( null );
  const animationFrameRef = useRef();

  const targetX = ( ( x * boardTileSize ) * renderScale ) + offsetX;
  const targetY = ( ( y * boardTileSize ) * renderScale ) + offsetY;

  let startX = targetX;
  let startY = targetY;
  let startAlpha = 1;
  let endX = targetX;
  let endY = targetY;
  let endAlpha = 1;
  let animStepIndex = 0;
  let duration;

  // TODO: Support different 'easing' functions

  let grabCurrentAnimStepStates = ( forceLast = false ) =>
  {
    let animStepData = forceLast ? animSteps[ animSteps.length - 1 ] : animSteps[ animStepIndex ];

    startX = targetX + ( animStepData.fromOffsetX !== undefined ? animStepData.fromOffsetX : 0 );
    startY = targetY + ( animStepData.fromOffsetY !== undefined ? animStepData.fromOffsetY : 0 );
    startAlpha = animStepData.fromAlpha !== undefined ? animStepData.fromAlpha : 1;

    endX = targetX + ( animStepData.toOffsetX !== undefined ? animStepData.toOffsetX : 0 );
    endY = targetY + ( animStepData.toOffsetY !== undefined ? animStepData.toOffsetY : 0 );
    endAlpha = animStepData.toAlpha !== undefined ? animStepData.toAlpha : 1;

    duration = animStepData.duration !== undefined ? animStepData.duration : ANIMATION_DURATION;

    if( !forceLast )
    {
      if( animStepData.loopFromIndex !== undefined
        && ( animStepData.loopIterations === undefined || animStepData.loopIterations > 0 )
      )
      {
        animStepIndex = animStepData.loopFromIndex;

        if( animStepData.loopIterations !== undefined )
        {
          animStepData.loopIterations--;
        }
      }
      else
      {
        animStepIndex++;
      }
    }
  };

  if( animSteps )
  {
    grabCurrentAnimStepStates( hasAnimated );
  }

  useEffect( () =>
  {
    if( textureCache == null || !textureCache[ imageSrc ] )
    {
      PIXI.Texture.fromURL( imageSrc ).then( ( tex ) =>
      {
        tex.baseTexture.mipmap = PIXI.MIPMAP_MODES.ON;
        tex.baseTexture.scaleMode = PIXI.SCALE_MODES.LINEAR;

        if( textureCache != null )
        {
          textureCache[ imageSrc ] = tex; // Store in cache
        }

        setTexture( tex );
      } ).catch( ( error ) =>
      {
        console.warn( `Failed to load texture: ${imageSrc}`, error );
        // Don't set texture on error, component will just not render
      } );
    }
    else
    {
      setTexture( textureCache[ imageSrc ] ); // Use cached texture
    }
  }, [ imageSrc, textureCache ] );

  // Get alpha data from shared cache when needed
  useEffect( () =>
  {
    if( props.alphaThreshold !== undefined && imageSrc )
    {
      // Check if alpha data is already cached
      const cachedAlphaData = spriteAlphaDataCache.getCachedAlphaData( imageSrc );
      if( cachedAlphaData )
      {
        setAlphaData( cachedAlphaData );
      }
      else
      {
        // Process alpha data asynchronously and cache it
        spriteAlphaDataCache.getAlphaData( imageSrc ).then( ( alphaData ) =>
        {
          // Check if component is still mounted before setting state
          if( spriteRef.current )
          {
            setAlphaData( alphaData );
          }
        } ).catch( ( error ) =>
        {
          console.warn( `Failed to get alpha data for ${imageSrc}:`, error );
          if( spriteRef.current )
          {
            setAlphaData( null );
          }
        } );
      }
    }
    else
    {
      setAlphaData( null );
    }
  }, [ imageSrc, props.alphaThreshold ] );

  useEffect( () =>
  {
    if( !texture || !spriteRef.current ) return;

    const sprite = spriteRef.current;

    if( !animSteps || hasAnimated )
    {
      sprite.alpha = props.alpha ?? endAlpha;
      sprite.x = endX;
      sprite.y = endY;

      return;
    }

    let startTime = performance.now();

    const animate = ( time ) =>
    {
      if( !spriteRef.current ) return;

      const elapsed = Math.max( time - ( startTime + delay ), 0 );
      const t = Math.min( elapsed / duration, 1 );
      const easedT = easeOutCubic( t );

      sprite.alpha = lerp( startAlpha, endAlpha, easedT );
      sprite.x = lerp( startX, endX, easedT );
      sprite.y = lerp( startY, endY, easedT );

      if( t < 1 )
      {
        animationFrameRef.current = requestAnimationFrame( animate );
      }
      else
      {
        if( animStepIndex < animSteps.length )
        {
          grabCurrentAnimStepStates();
          startTime = performance.now();
          animationFrameRef.current = requestAnimationFrame( animate );
        }
        else
        {
          setHasAnimated( true );
          if( onAnimFinish != null )
          {
            onAnimFinish();
          }
        }
      }
    };

    // Initial values
    sprite.alpha = startAlpha;
    sprite.x = startX;
    sprite.y = startY;

    animationFrameRef.current = requestAnimationFrame( animate );

    return () => cancelAnimationFrame( animationFrameRef.current );

  } );

  // Configure custom hit area or alpha threshold detection
  useEffect( () =>
  {
    if( spriteRef.current && props.onClick )
    {
      const sprite = spriteRef.current;

      if( props.customHitArea )
      {
        if( props.customHitArea.type === 'circle' )
        {
          sprite.hitArea = new PIXI.Circle( 0, 0, props.customHitArea.radius );
        } else if( props.customHitArea.type === 'rectangle' )
        {
          sprite.hitArea = new PIXI.Rectangle(
            props.customHitArea.x,
            props.customHitArea.y,
            props.customHitArea.width,
            props.customHitArea.height
          );
        }
      } else if( props.alphaThreshold !== undefined && alphaData )
      {
        // Implement efficient alpha threshold hit detection using cached alpha data
        sprite.hitArea = {
          contains: ( x, y ) =>
          {
            try
            {
              // Convert sprite-relative coordinates to texture coordinates
              const localBounds = sprite.getLocalBounds();
              const texture = sprite.texture;

              if( !texture || !alphaData || !alphaData.data ) return false;

              // Calculate texture coordinates
              const textureX = ( ( x - localBounds.x ) / localBounds.width ) * texture.width;
              const textureY = ( ( y - localBounds.y ) / localBounds.height ) * texture.height;

              // Check bounds
              if( textureX < 0 || textureX >= alphaData.width || textureY < 0 || textureY >= alphaData.height )
              {
                return false;
              }

              // Get alpha value from cached data (very fast lookup)
              const pixelIndex = Math.floor( textureY ) * alphaData.width + Math.floor( textureX );

              // Additional bounds check for the pixel index
              if( pixelIndex < 0 || pixelIndex >= alphaData.data.length )
              {
                return false;
              }

              const alpha = alphaData.data[ pixelIndex ] / 255; // Normalize to 0-1

              return alpha >= props.alphaThreshold;
            }
            catch( error )
            {
              console.warn( 'Error in alpha threshold hit detection:', error );
              return false; // Fail safe - don't register the click
            }
          }
        };
      }
    }
  }, [ texture, props.onClick, props.customHitArea, props.alphaThreshold, alphaData ] );

  // Cleanup effect to handle component unmounting
  useEffect( () =>
  {
    return () =>
    {
      // Clean up any ongoing animation frames when component unmounts
      if( animationFrameRef.current )
      {
        cancelAnimationFrame( animationFrameRef.current );
      }
    };
  }, [] );

  if( !texture ) return null;

  return (
    <Sprite
      ref={ spriteRef }
      texture={ texture }
      scale={ renderScale }
      x={ hasAnimated ? endX : startX }
      y={ hasAnimated ? endY : startY }
      anchor={ props.anchor ?? 0.5 }
      eventMode={ props.onClick ? 'static' : 'none' }
      pointerdown={ props.onClick ?? null }
      tint={ props.tint ?? 0xffffff }
      angle={ props.angle ?? 0 }
      alpha={ props.alpha ?? 1 }
      filters={ props.filters ?? [] }
    />
  );
};

export default BoardSprite;

